import React from 'react';
import { useParams, Link } from 'react-router-dom';
import HeroSection from '../components/HeroSection';
import SectionTitle from '../components/SectionTitle';
import { BrainCircuitIcon, ServerIcon, CodeIcon } from 'lucide-react';
const ProductDetail = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const products = {
    ph8: {
      title: 'PH8 大模型MaaS平台',
      subtitle: '企业级大模型即服务平台，赋能各行业AI应用',
      description: `PH8是我们自主研发的大模型即服务(MaaS)平台，为企业和开发者提供强大的AI能力。平台集成了多种先进的大语言模型，支持自然语言处理、内容生成、知识问答等多种场景。
通过PH8平台，企业可以轻松接入大模型能力，无需复杂的技术部署，即可快速构建智能应用。平台提供简洁的API接口和丰富的SDK工具，支持多种编程语言和开发环境。`,
      features: [{
        title: '多模型集成与管理',
        description: '集成国内外领先的开源和闭源大模型，统一管理和调用接口，简化多模型应用开发。'
      }, {
        title: '企业级安全与隐私保护',
        description: '严格的数据隔离和安全保障机制，支持数据加密传输和存储，保护企业核心数据安全。'
      }, {
        title: '高性能推理引擎',
        description: '优化的推理引擎，提供低延迟、高并发的服务能力，满足企业级应用的性能需求。'
      }, {
        title: '丰富的API和SDK支持',
        description: '提供RESTful API和多语言SDK，支持Python、Java、Go等主流编程语言，简化接入流程。'
      }, {
        title: '定制化模型训练与微调',
        description: '支持基于企业数据的模型微调和定制化训练，提升模型在特定领域的表现。'
      }, {
        title: '细粒度的成本控制',
        description: '提供详细的用量统计和成本分析，支持多种计费模式，帮助企业优化AI应用成本。'
      }],
      specifications: [{
        name: '支持模型',
        value: 'GPT-4, LLaMA2, Deepseek, Qwen, GLM等'
      }, {
        name: 'API协议',
        value: 'RESTful API, gRPC'
      }, {
        name: 'SDK支持',
        value: 'Python, Java, JavaScript, Go, C#'
      }, {
        name: '部署方式',
        value: '公有云, 私有云, 混合云, 本地部署'
      }, {
        name: '安全认证',
        value: 'ISO27001, SOC2, GDPR合规'
      }, {
        name: 'SLA保障',
        value: '99.9%服务可用性'
      }],
      image: 'https://images.unsplash.com/photo-1677442135968-6d89469c177e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      icon: <BrainCircuitIcon className="h-12 w-12" data-id="element-500" />,
      cases: [{
        title: '金融行业智能风控系统',
        description: '为某大型银行构建基于大模型的智能风控系统，提高风险识别准确率40%，减少人工审核时间60%。',
        link: '/cases/llm/case1'
      }, {
        title: '智能客服升级项目',
        description: '帮助某电商平台升级智能客服系统，接入大模型能力，提升复杂问题解决率35%，客户满意度提升25%。',
        link: '/cases/llm/case2'
      }, {
        title: '法律文档智能分析系统',
        description: '为某律师事务所开发法律文档智能分析系统，自动提取关键信息和风险点，提高工作效率50%以上。',
        link: '/cases/llm/case3'
      }]
    },
    cloud: {
      title: '普华云 异构算力服务平台',
      subtitle: '高性能异构算力平台，满足AI训练与推理需求',
      description: `普华云是我们打造的异构算力服务平台，整合了GPU、NPU、TPU等多种异构计算资源，为AI训练和推理提供高性能、低延迟的计算服务。
平台采用先进的资源调度和负载均衡技术，实现计算资源的高效利用和动态扩展。用户可以根据实际需求灵活选择不同类型和规格的算力资源，按需付费，大幅降低AI应用的开发和运营成本。`,
      features: [{
        title: '多种异构算力资源',
        description: '支持NVIDIA A100/H100、国产GPU和NPU等多种算力设备，满足不同AI任务的需求。'
      }, {
        title: '智能资源调度系统',
        description: '基于任务特性自动选择最优算力资源，提高资源利用率和任务执行效率。'
      }, {
        title: '弹性扩缩容能力',
        description: '支持资源的自动扩缩容，根据负载情况动态调整资源配置，满足峰值需求。'
      }, {
        title: '完善的监控和运维工具',
        description: '提供实时监控和故障自动处理能力，确保服务的稳定性和可靠性。'
      }, {
        title: '高性能网络架构',
        description: '采用高带宽、低延迟的网络连接，提升数据传输效率，加速分布式训练。'
      }, {
        title: '按需付费的灵活计费模式',
        description: '支持按小时、按任务等多种计费方式，优化成本结构，提高资源使用效益。'
      }],
      specifications: [{
        name: 'GPU型号',
        value: 'NVIDIA A100, H100, A10, T4等'
      }, {
        name: '国产芯片支持',
        value: '昆仑芯、天数智芯、寒武纪等'
      }, {
        name: '存储系统',
        value: '高性能分布式存储, 本地NVMe SSD'
      }, {
        name: '网络带宽',
        value: '100Gbps RDMA, 25Gbps以太网'
      }, {
        name: '监控系统',
        value: 'Prometheus + Grafana, 自研监控平台'
      }, {
        name: '支持框架',
        value: 'PyTorch, TensorFlow, MindSpore, PaddlePaddle等'
      }],
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2034&q=80',
      icon: <ServerIcon className="h-12 w-12" data-id="element-501" />,
      cases: [{
        title: '大规模AI模型训练平台',
        description: '为某研究机构提供大规模分布式训练平台，支持千亿参数模型训练，训练速度提升3倍。',
        link: '/cases/computing/case1'
      }]
    },
    wanli: {
      title: '万里 大模型开发平台',
      subtitle: '一站式大模型开发环境，加速AI应用落地',
      description: `万里大模型开发平台是一个一站式的大模型开发环境，涵盖了数据处理、模型训练、评估、部署的全流程工具链，大幅简化大模型开发流程，加速企业AI应用落地。
平台提供直观的可视化界面和完善的开发工具，支持开发者快速构建和优化大模型应用。同时，平台内置丰富的预训练模型和数据集，帮助开发者快速启动项目，避免从零开始的高成本研发。`,
      features: [{
        title: '全流程AI开发工具链',
        description: '从数据处理到模型部署的完整工具支持，覆盖AI开发全生命周期。'
      }, {
        title: '低代码可视化开发环境',
        description: '可视化界面和组件化设计，降低开发门槛，提高开发效率。'
      }, {
        title: '丰富的预训练模型库',
        description: '内置多种预训练模型和领域模型，支持快速迁移学习和微调。'
      }, {
        title: '高效分布式训练框架',
        description: '优化的分布式训练框架，支持多机多卡训练，提高训练效率。'
      }, {
        title: '全面的模型评估与优化工具',
        description: '提供多维度的评估指标和优化建议，帮助提升模型性能。'
      }, {
        title: '一键部署到多种环境',
        description: '支持模型一键部署到云端、边缘设备和移动端等多种环境。'
      }],
      specifications: [{
        name: '支持框架',
        value: 'PyTorch, TensorFlow, JAX等'
      }, {
        name: '内置模型',
        value: '超过50种预训练模型和领域模型'
      }, {
        name: '数据处理',
        value: '自动数据清洗、增强、标注工具'
      }, {
        name: '训练加速',
        value: '混合精度训练, 模型并行, 数据并行'
      }, {
        name: '部署支持',
        value: '云服务, Docker容器, 边缘设备, 移动端'
      }, {
        name: '集成开发环境',
        value: 'Web IDE, Jupyter Notebook, VSCode插件'
      }],
      image: 'https://images.unsplash.com/photo-1555949963-ff9fe0c870eb?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80',
      icon: <CodeIcon className="h-12 w-12" data-id="element-502" />,
      cases: []
    }
  };
  const product = id ? products[id as keyof typeof products] : null;
  if (!product) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-503">
        <div className="text-center" data-id="element-504">
          <h2 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-505">产品不存在</h2>
          <p className="text-gray-600 mb-6" data-id="element-506">您查找的产品不存在或已被移除。</p>
          <Link to="/products" className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-medium py-2 px-6 rounded-md transition duration-300" data-id="element-507">
            返回产品列表
          </Link>
        </div>
      </div>;
  }
  return <div className="bg-white" data-id="element-508">
      <HeroSection title={product.title} subtitle={product.subtitle} backgroundImage={product.image} size="medium" data-id="element-509" />
      <section className="py-16 md:py-24" data-id="element-510">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-511">
          <div className="flex flex-col md:flex-row gap-12" data-id="element-512">
            <div className="w-full md:w-2/3" data-id="element-513">
              <div className="mb-12" data-id="element-514">
                <SectionTitle title="产品概述" alignment="left" titleSize="medium" data-id="element-515" />
                <div className="prose prose-lg max-w-none" data-id="element-516">
                  {product.description.split('\n\n').map((paragraph, index) => <p key={index} className="mb-4 text-gray-700" data-id="element-517">
                      {paragraph}
                    </p>)}
                </div>
              </div>
              <div className="mb-12" data-id="element-518">
                <SectionTitle title="核心功能" alignment="left" titleSize="medium" data-id="element-519" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-520">
                  {product.features.map((feature, index) => <div key={index} className="bg-white p-6 rounded-lg border border-gray-200 hover:border-primary-500 transition-colors duration-300" data-id="element-521">
                      <h3 className="text-xl font-semibold mb-3 text-gray-900" data-id="element-522">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600" data-id="element-523">{feature.description}</p>
                    </div>)}
                </div>
              </div>
              {product.cases && product.cases.length > 0 && <div className="mb-12" data-id="element-524">
                  <SectionTitle title="相关案例" alignment="left" titleSize="medium" data-id="element-525" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-526">
                    {product.cases.map((caseItem, index) => <Link key={index} to={caseItem.link} className="block bg-white p-6 rounded-lg border border-gray-200 hover:border-primary-500 hover:shadow-md transition-all duration-300" data-id="element-527">
                        <h3 className="text-xl font-semibold mb-3 text-gray-900" data-id="element-528">
                          {caseItem.title}
                        </h3>
                        <p className="text-gray-600 mb-4" data-id="element-529">
                          {caseItem.description}
                        </p>
                        <span className="text-primary-600 font-medium inline-flex items-center" data-id="element-530">
                          查看详情
                          <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" data-id="element-531">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" data-id="element-532"></path>
                          </svg>
                        </span>
                      </Link>)}
                  </div>
                </div>}
            </div>
            <div className="w-full md:w-1/3" data-id="element-533">
              <div className="bg-gray-50 p-6 rounded-lg sticky top-24" data-id="element-534">
                <div className="text-primary-500 mb-4" data-id="element-535">{product.icon}</div>
                <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-536">
                  技术规格
                </h3>
                <div className="space-y-4" data-id="element-537">
                  {product.specifications.map((spec, index) => <div key={index} className="border-b border-gray-200 pb-3" data-id="element-538">
                      <div className="text-sm font-medium text-gray-500 mb-1" data-id="element-539">
                        {spec.name}
                      </div>
                      <div className="text-base text-gray-900" data-id="element-540">
                        {spec.value}
                      </div>
                    </div>)}
                </div>
                <div className="mt-8" data-id="element-541">
                  <Link to="/about/contact" className="inline-block w-full bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-6 rounded-md text-center transition duration-300" data-id="element-542">
                    咨询详情
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-16 md:py-24 bg-gray-50" data-id="element-543">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-544">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6" data-id="element-545">
            准备好开始使用了吗？
          </h2>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto" data-id="element-546">
            联系我们的技术团队，了解更多产品细节和定制化方案。
          </p>
          <div className="flex flex-col sm:flex-row justify-center space-y-4 sm:space-y-0 sm:space-x-4" data-id="element-547">
            <Link to="/about/contact" className="inline-block bg-primary-600 hover:bg-primary-700 text-white font-medium py-3 px-8 rounded-md transition duration-300" data-id="element-548">
              联系我们
            </Link>
            <Link to="/products" className="inline-block bg-white text-primary-600 font-medium py-3 px-8 rounded-md border border-primary-600 transition duration-300 hover:bg-primary-50" data-id="element-549">
              返回产品列表
            </Link>
          </div>
        </div>
      </section>
    </div>;
};
export default ProductDetail;